/**
 * @fileoverview Module for `src/admin/LuZips.js`.
 */

import React, { Fragment } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Filter,
  TextInput,
  NumberInput,
  BulkExportButton,
  Create,
  SimpleForm,
  required,
  regex,
  Toolbar,
  SaveButton,
  Edit,
  EditButton,
  useNotify,
} from 'react-admin';


/**
 * Displays API errors for lookup zip create and edit mutations.
 *
 * @param {Object} error
 * @param {Function} notify
 * @returns {void}
 */
const errorHandler = (error, notify) => {
  if (error.status === 'Conflict') {
    notify(`Error: Zip must be Unique`);
  } else {
    notify(`Error: ${ error.body || error.message }`);
  }
};

/**
 * Shared form inputs for lookup zip create and edit views.
 *
 * @returns {JSX.Element} Zip metadata fields.
 */
const SharedInputs = () => (
  <Fragment>
    <TextInput source='city' validate={ [required()] } /> <br />
    <TextInput label='State Abbreviation' source='state' validate={ [required(), regex(/^[A-Z]{2}$/, 'State should be two uppercase characters')] } /> <br />
    <NumberInput source='lat' /> <br />
    <NumberInput source='long' /> <br />
  </Fragment>
);

/**
 * Toolbar used by the lookup zip create form.
 *
 * @param {Object} props
 * @returns {JSX.Element} Create toolbar.
 */
const LuZipCreateToolbar = (props) => (
  <Toolbar { ...props } >
    <SaveButton
      label='create'
    />
  </Toolbar>
);

/**
 * Toolbar used by the lookup zip edit form.
 *
 * @param {Object} props
 * @returns {JSX.Element} Edit toolbar.
 */
const LuZipEditToolbar = (props) => (
  <Toolbar { ...props } >
    <SaveButton />
  </Toolbar>
);

/**
 * Edit view for lookup zip records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Lookup zip editor.
 */
export const LuZipEdit = (props) => {
  const notify = useNotify();
  /**
   * Delegates lookup zip edit errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };

  return (
    <Edit onFailure={ onFailure } undoable={ false } { ...props } toolbar={ <LuZipEditToolbar /> }>
      <SimpleForm redirect='edit' toolbar={ <LuZipEditToolbar /> }>
        <TextInput disabled source='zip' validate={ [required()] } />
        <SharedInputs />
      </SimpleForm>
    </Edit>
  );
};

/**
 * Create view for lookup zip records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Lookup zip creation form.
 */
export const LuZipCreate = (props) => {
  const notify = useNotify();
  /**
   * Delegates lookup zip creation errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };
  return (
    <Create onFailure={ onFailure } { ...props } toolbar={ <LuZipCreateToolbar /> }>
      <SimpleForm toolbar={ <LuZipCreateToolbar /> }>
        <TextInput source='zip' validate={ [regex(/^\d{5}$/, 'zip should be 5 digits')] } />
        <SharedInputs />
      </SimpleForm>
    </Create>
  );
};

/**
 * Bulk action controls for lookup zip lists.
 *
 * @param {Object} props
 * @returns {JSX.Element} Bulk action button group.
 */
const LuZipListBulkActionButtons = (props) => (
  <BulkExportButton { ...props } />
);

/**
 * Filter controls for lookup zip list queries.
 *
 * @param {Object} props
 * @returns {JSX.Element} Zip list filters.
 */
const LuZipFilter = (props) => (
  <Filter { ...props }>
    <TextInput alwaysOn label='Zip' source='zip:startsWith' />
  </Filter>
);

/**
 * List view for lookup zip records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Lookup zip table.
 */
const LuZipList = (props) => (
  <List
    { ...props }
    bulkActionButtons={ <LuZipListBulkActionButtons /> }
    filters={ <LuZipFilter /> }
    sort={{ field: 'zip', order: 'ASC' }}
  >
    <Datagrid>
      <TextField source='zip' />
      <TextField source='state' />
      <TextField source='city' />
      <EditButton { ...props } />
    </Datagrid>
  </List>
);

export default LuZipList;
